import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Spinner } from '@tempo-io/sui-spinner';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/common/components/header/Header';
import { idQueryKeys } from '@/common/queryKeys/id';
import i18n from '@/common/translations/i18n';
import { InvitesList } from '@/features/invites/components/InvitesList';
import { getPendingInvites } from '@/features/settings/user-management/api/invitesApi';
import { router } from '@/router';

export const Route = createFileRoute('/(app)/invites/')({
  component: AcceptInvitesScreen,
  staticData: {
    topNav: {
      title: i18n.t(($) => $.invites.topNav.title),
      description: i18n.t(($) => $.invites.topNav.description),
    },
  },
});

export function AcceptInvitesScreen() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: idQueryKeys.invites.pending,
    queryFn: () => getPendingInvites(),
  });

  const invites = data?.invites ?? [];

  // Accepting joins the org, which for an SSO-only invitee to a non-SSO org needs a password —
  // so defer to the auto-accept screen, which resolves the connection (silent / link / add
  // password) exactly as the invite-email flow does.
  const handleAcceptInvite = useCallback((invite: { id: number }) => {
    router.navigate({ to: '/invites/auto-accept', search: { inviteId: invite.id } });
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col overflow-auto">
      <Header
        title={t(($) => $.invites.topNav.title)}
        description={t(($) => $.invites.topNav.description)}
      />
      <div className="container px-6 py-8 max-w-4xl">
        <InvitesList invites={invites} onAcceptInvite={handleAcceptInvite} />
      </div>
    </div>
  );
}
